import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';
import { getMaterialProjectName } from '../../../getMaterialProjectName';

interface PipeFilters {
  status: string[];
  pipeNo: string[];
  pipeNRI: string[];
  pipeProjectStage: string[];
  heatNumber: string[];
  outerDiameter: string[];
  wallThickness: string[];
  projectName: string[];
}

interface UsePipeFiltersProps {
  materials: MaterialData[];
}

function dimensionLabel(value: unknown): string | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(n);
}

function sortDimensionLabels(values: string[]): string[] {
  return [...values].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });
}

export const usePipeFilters = ({ materials }: UsePipeFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<PipeFilters>({
    status: [],
    pipeNo: [],
    pipeNRI: [],
    pipeProjectStage: [],
    heatNumber: [],
    outerDiameter: [],
    wallThickness: [],
    projectName: [],
  });

  // Extract unique pipe numbers from materials
  const uniquePipeNumbers = useMemo(() => {
    const pipeNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.pipeNo) {
        pipeNumbers.add(material.details.pipeNo);
      }
    });
    return Array.from(pipeNumbers).sort();
  }, [materials]);

  // Extract unique pipe NRIs from materials
  const uniquePipeNRIs = useMemo(() => {
    const pipeNRIs = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.pipeNRI) {
        pipeNRIs.add(material.details.pipeNRI);
      }
    });
    return Array.from(pipeNRIs).sort();
  }, [materials]);

  // Extract unique pipe project stages from materials
  const uniquePipeProjectStages = useMemo(() => {
    const pipeProjectStages = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.pipeProjectStage) {
        pipeProjectStages.add(material.details.pipeProjectStage);
      }
    });
    return Array.from(pipeProjectStages).sort();
  }, [materials]);

  // Extract unique heat numbers from materials
  const uniqueHeatNumbers = useMemo(() => {
    const heatNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.pipeHeatNumber) {
        heatNumbers.add(material.details.pipeHeatNumber);
      }
    });
    return Array.from(heatNumbers).sort();
  }, [materials]);

  const uniqueOuterDiameters = useMemo(() => {
    const values = new Set<string>();
    materials.forEach((material: MaterialData) => {
      const label = dimensionLabel(material.details?.pipeOuterDiameter);
      if (label) values.add(label);
    });
    return sortDimensionLabels(Array.from(values));
  }, [materials]);

  const uniqueWallThicknesses = useMemo(() => {
    const values = new Set<string>();
    materials.forEach((material: MaterialData) => {
      const label = dimensionLabel(material.details?.pipeWallThickness);
      if (label) values.add(label);
    });
    return sortDimensionLabels(Array.from(values));
  }, [materials]);

  const uniqueProjectNames = useMemo(() => {
    const names = new Set<string>();
    materials.forEach((material: MaterialData) => {
      const name = getMaterialProjectName(material as unknown as Record<string, unknown>);
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  // Filter materials based on search query and filters
  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      const projectName = getMaterialProjectName(material as unknown as Record<string, unknown>);

      // Search query filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        material.materialId?.toString().includes(searchLower) ||
        material.details?.pipeNRI?.toLowerCase().includes(searchLower) ||
        material.assemblyNumbers?.some((assembly: string) => assembly?.toLowerCase().includes(searchLower)) ||
        material.details?.pipeNo?.toLowerCase().includes(searchLower) ||
        material.details?.pipeHeatNumber?.toLowerCase().includes(searchLower) ||
        projectName.toLowerCase().includes(searchLower) ||
        (material.materialStatus)?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(material.materialStatus || '');

      // Pipe number filter
      const matchesPipeNo = filters.pipeNo.length === 0 || 
        (material.details?.pipeNo && filters.pipeNo.includes(material.details.pipeNo));

      // Pipe NRI filter
      const matchesPipeNRI = filters.pipeNRI.length === 0 || 
        (material.details?.pipeNRI && filters.pipeNRI.includes(material.details.pipeNRI));

      // Pipe project stage filter
      const matchesPipeProjectStage = filters.pipeProjectStage.length === 0 || 
        (material.details?.pipeProjectStage && filters.pipeProjectStage.includes(material.details.pipeProjectStage));

      // Heat number filter
      const matchesHeatNumber = filters.heatNumber.length === 0 || 
        (material.details?.pipeHeatNumber && filters.heatNumber.includes(material.details.pipeHeatNumber));

      const odLabel = dimensionLabel(material.details?.pipeOuterDiameter);
      const matchesOuterDiameter =
        filters.outerDiameter.length === 0 ||
        (odLabel != null && filters.outerDiameter.includes(odLabel));

      const wtLabel = dimensionLabel(material.details?.pipeWallThickness);
      const matchesWallThickness =
        filters.wallThickness.length === 0 ||
        (wtLabel != null && filters.wallThickness.includes(wtLabel));

      const matchesProjectName =
        filters.projectName.length === 0 ||
        (projectName !== '' && filters.projectName.includes(projectName));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPipeNo &&
        matchesPipeNRI &&
        matchesPipeProjectStage &&
        matchesHeatNumber &&
        matchesOuterDiameter &&
        matchesWallThickness &&
        matchesProjectName
      );
    });
  }, [materials, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.status.length > 0 || 
           filters.pipeNo.length > 0 ||
           filters.pipeNRI.length > 0 ||
           filters.pipeProjectStage.length > 0 ||
           filters.heatNumber.length > 0 ||
           filters.outerDiameter.length > 0 ||
           filters.wallThickness.length > 0 ||
           filters.projectName.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof PipeFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      pipeNo: [],
      pipeNRI: [],
      pipeProjectStage: [],
      heatNumber: [],
      outerDiameter: [],
      wallThickness: [],
      projectName: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniquePipeNumbers,
    uniquePipeNRIs,
    uniquePipeProjectStages,
    uniqueHeatNumbers,
    uniqueOuterDiameters,
    uniqueWallThicknesses,
    uniqueProjectNames,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
}; 